import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import config from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { StockMovement } from '../types/stockMovements.types';
import { stockMovementsService } from '../services/stockMovements.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { fmtUnitPrice } from '../utils/formatNumber';

const translations = {
  en: {
    title: 'Stock Movements',
    movementType: 'Movement Type',
    referenceNumber: 'Reference',
    movementDate: 'Date',
    item: 'Item',
    location: 'Location',
    quantityIn: 'In',
    quantityOut: 'Out',
    unitCost: 'Unit Cost',
    totalValue: 'Total Value',
    notes: 'Notes',
    noData: 'No stock movements found',
    fromDate: 'From Date',
    toDate: 'To Date',
    all: 'All Types',
    purchaseInvoice: 'Purchase Invoice',
    stockIssue: 'Stock Issue',
    warehouseTransferIn: 'Warehouse Transfer In',
    warehouseTransferOut: 'Warehouse Transfer Out',
    branchTransferIn: 'Branch Transfer In',
    branchTransferOut: 'Branch Transfer Out',
    stockCount: 'Stock Count',
  },
  ar: {
    title: 'حركات المخزون',
    movementType: 'نوع الحركة',
    referenceNumber: 'المرجع',
    movementDate: 'التاريخ',
    item: 'الصنف',
    location: 'الموقع',
    quantityIn: 'وارد',
    quantityOut: 'صادر',
    unitCost: 'سعر الوحدة',
    totalValue: 'القيمة الإجمالية',
    notes: 'ملاحظات',
    noData: 'لا توجد حركات مخزون',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    all: 'جميع الأنواع',
    purchaseInvoice: 'فاتورة شراء',
    stockIssue: 'صرف مخزون',
    warehouseTransferIn: 'تحويل مخزن وارد',
    warehouseTransferOut: 'تحويل مخزن صادر',
    branchTransferIn: 'تحويل فرع وارد',
    branchTransferOut: 'تحويل فرع صادر',
    stockCount: 'جرد',
  },
};

const StockMovementsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [filters, setFilters] = useState({
    movementType: '',
    fromDate: '',
    toDate: '',
  });

  const movementTypes = [
    { value: '', label: t.all },
    { value: 'PurchaseInvoice', label: t.purchaseInvoice },
    { value: 'StockIssue', label: t.stockIssue },
    { value: 'WarehouseTransfer-In', label: t.warehouseTransferIn },
    { value: 'WarehouseTransfer-Out', label: t.warehouseTransferOut },
    { value: 'BranchTransfer-In', label: t.branchTransferIn },
    { value: 'BranchTransfer-Out', label: t.branchTransferOut },
    { value: 'StockCount', label: t.stockCount },
  ];

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  useEffect(() => {
    loadMovements();
  }, [filters]);

  const loadMovements = async () => {
    setLoading(true);
    try {
      const data = await stockMovementsService.getAll(
        filters.movementType || undefined,
        undefined,
        undefined,
        undefined,
        filters.fromDate || undefined,
        filters.toDate || undefined
      );
      setMovements(data);
    } catch (error) {
      console.error('Failed to load stock movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMovementTypeColor = (type: string) => {
    if (type === 'PurchaseInvoice') return 'success';
    if (type === 'StockIssue') return 'error';
    if (type.includes('Transfer-In') || type === 'BranchTransfer-In') return 'info';
    if (type.includes('Transfer-Out') || type === 'BranchTransfer-Out') return 'warning';
    if (type === 'StockCount') return 'default';
    return 'default';
  };

  const getMovementTypeLabel = (type: string) => {
    switch (type) {
      case 'PurchaseInvoice':
        return t.purchaseInvoice;
      case 'StockIssue':
        return t.stockIssue;
      case 'WarehouseTransfer-In':
        return t.warehouseTransferIn;
      case 'WarehouseTransfer-Out':
        return t.warehouseTransferOut;
      case 'BranchTransfer-In':
        return t.branchTransferIn;
      case 'BranchTransfer-Out':
        return t.branchTransferOut;
      case 'StockCount':
        return t.stockCount;
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const getLocation = (movement: StockMovement) => {
    if (movement.branchId) {
      return isRTL ? movement.branchNameAr : movement.branchName;
    }
    if (movement.warehouseId) {
      return isRTL ? movement.warehouseNameAr : movement.warehouseName;
    }
    return '-';
  };

  const handlePrint = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const docTitle = isRTL ? 'حركات المخزون' : 'Stock Movements';

    const filterInfo = [];
    if (filters.movementType) {
      const typeLabel = movementTypes.find(mt => mt.value === filters.movementType)?.label || filters.movementType;
      filterInfo.push(`${t.movementType}: ${typeLabel}`);
    }
    if (filters.fromDate) filterInfo.push(`${t.fromDate}: ${new Date(filters.fromDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}`);
    if (filters.toDate) filterInfo.push(`${t.toDate}: ${new Date(filters.toDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}`);

    const rows = movements.map((m, i) => `
      <tr>
        <td style="text-align:center;">${i + 1}</td>
        <td>${getMovementTypeLabel(m.movementType)}</td>
        <td><strong>${m.referenceNumber}</strong></td>
        <td>${new Date(m.movementDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</td>
        <td><strong>${isRTL ? m.itemNameAr : m.itemName}</strong><br/><span style="color:#666;font-size:10px;">${m.itemCode}</span></td>
        <td>${getLocation(m)}</td>
        <td style="text-align:right;color:#2e7d32;font-weight:bold;">${m.quantityIn > 0 ? '+' + m.quantityIn.toFixed(2) : ''}</td>
        <td style="text-align:right;color:#d32f2f;font-weight:bold;">${m.quantityOut > 0 ? '-' + m.quantityOut.toFixed(2) : ''}</td>
        <td style="text-align:right;">${fmtUnitPrice(m.unitCost)}</td>
        <td style="text-align:right;font-weight:bold;">${m.totalValue.toFixed(2)}</td>
      </tr>
    `).join('');

    const totalIn = movements.reduce((s, m) => s + m.quantityIn, 0);
    const totalOut = movements.reduce((s, m) => s + m.quantityOut, 0);
    const totalValue = movements.reduce((s, m) => s + m.totalValue, 0);

    const html = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${docTitle}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; font-size: 12px; color: #333; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 11px; }
          th { background: #1976d2; color: white; font-weight: bold; }
          tr:nth-child(even) { background: #f9f9f9; }
          .filter-info { margin-bottom: 10px; font-size: 11px; color: #666; }
          @page { size: A4 landscape; margin: 1cm; }
        </style>
      </head>
      <body>
        ${companyHeader}
        <h1 style="text-align:center;color:#1976d2;margin:10px 0 15px;font-size:20px;">${docTitle}</h1>
        ${filterInfo.length > 0 ? `<div class="filter-info">${filterInfo.join(' &nbsp;|&nbsp; ')}</div>` : ''}
        <div style="font-size:11px;color:#666;margin-bottom:8px;">${isRTL ? 'عدد الحركات' : 'Total Records'}: ${movements.length}</div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${t.movementType}</th>
              <th>${t.referenceNumber}</th>
              <th>${t.movementDate}</th>
              <th>${t.item}</th>
              <th>${t.location}</th>
              <th>${t.quantityIn}</th>
              <th>${t.quantityOut}</th>
              <th>${t.unitCost}</th>
              <th>${t.totalValue}</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
            <tr style="background:#e3f2fd;font-weight:bold;">
              <td colspan="6" style="text-align:${isRTL ? 'left' : 'right'};">${isRTL ? 'الإجمالي:' : 'Total:'}</td>
              <td style="text-align:right;color:#2e7d32;">${totalIn > 0 ? '+' + totalIn.toFixed(2) : ''}</td>
              <td style="text-align:right;color:#d32f2f;">${totalOut > 0 ? '-' + totalOut.toFixed(2) : ''}</td>
              <td></td>
              <td style="text-align:right;">${totalValue.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=1100,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h4">{t.title}</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadMovements} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={movements.length === 0}
          size="small"
          sx={{ ml: 'auto' }}
        >
          {isRTL ? 'طباعة القائمة' : 'Print List'}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(3, 1fr)' 
          }, 
          gap: 2 
        }}>
          <TextField
            fullWidth
            select
            label={t.movementType}
            value={filters.movementType}
            onChange={(e) => setFilters({ ...filters, movementType: e.target.value })}
            size="medium"
            SelectProps={{
              displayEmpty: true,
            }}
          >
            {movementTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={filters.fromDate ? new Date(filters.fromDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFilters(prev => ({ ...prev, fromDate: newVal, ...(prev.toDate && newVal && prev.toDate < newVal ? { toDate: '' } : {}) }));
              }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={filters.toDate ? new Date(filters.toDate) : maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={filters.toDate ? new Date(filters.toDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFilters(prev => ({ ...prev, toDate: newVal, ...(prev.fromDate && newVal && prev.fromDate > newVal ? { fromDate: '' } : {}) }));
              }}
              format="dd/MM/yyyy"
              minDate={filters.fromDate ? new Date(filters.fromDate) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" sx={{ minWidth: { xs: 1000, md: 1200 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}>{t.movementType}</TableCell>
                <TableCell sx={{ minWidth: 120 }}>{t.referenceNumber}</TableCell>
                <TableCell sx={{ minWidth: 120 }}>{t.movementDate}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>{t.item}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{t.location}</TableCell>
                <TableCell sx={{ minWidth: 100 }} align="right">{t.quantityIn}</TableCell>
                <TableCell sx={{ minWidth: 100 }} align="right">{t.quantityOut}</TableCell>
                <TableCell sx={{ minWidth: 100 }} align="right">{t.unitCost}</TableCell>
                <TableCell sx={{ minWidth: 120 }} align="right">{t.totalValue}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{t.notes}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.map((movement) => (
                <TableRow key={`${movement.movementType}-${movement.movementId}`}>
                  <TableCell>
                    <Chip
                      label={getMovementTypeLabel(movement.movementType)}
                      color={getMovementTypeColor(movement.movementType)}
                      size="small"
                      sx={{ minWidth: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.9rem" fontWeight="bold" color="primary">
                      {movement.referenceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.85rem">
                      {formatDate(movement.movementDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.9rem" fontWeight="500">
                      {isRTL ? movement.itemNameAr : movement.itemName}
                    </Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">
                      {movement.itemCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.85rem">
                      {getLocation(movement)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {movement.quantityIn > 0 && (
                      <Typography fontSize="0.9rem" fontWeight="bold" color="success.main">
                        +{movement.quantityIn.toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {movement.quantityOut > 0 && (
                      <Typography fontSize="0.9rem" fontWeight="bold" color="error.main">
                        -{movement.quantityOut.toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.85rem">
                      {fmtUnitPrice(movement.unitCost)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.9rem" fontWeight="bold">
                      {movement.totalValue.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.8rem" color="text.secondary">
                      {movement.notes || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {movements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {t.noData}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default StockMovementsPage;